import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import { evictLicenseQueries } from "@/v2-backoffice-client/license/license.utils"
import { LICENSE_STATUS_ACTIVE } from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseConvertMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// The explicit convert step of D3: a trial never silently becomes paid — an
// operator flips it to Active once the deal is done. TrialEndsAt stays on the
// row as the record of when the trial would have ended. Once the Fenerum
// subscription starts billing the slot, the hourly sync takes the license
// over (it becomes Fenerum-sourced and read-only here).
function LicenseConvertMenuItem(props: LicenseConvertMenuItemProps) {
  const { license, ...menuItemProps } = props

  const [updateLicense, { loading, error }] = useLicenseUpdateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      toast({
        variant: "success",
        title: "Trial converted",
        description: `${data.updateLicense.CustomerName ?? "The customer"} is now on an active ${data.updateLicense.ProductName} license.`,
      })
    },
  })

  return (
    <ActionMenu.ConfirmItem
      iconSrc="check"
      isLoading={loading}
      error={error}
      errorTitle="Failed to convert trial"
      confirmTitle="Convert Trial to Active"
      confirmDescription={`Marks ${license.CustomerName ?? "this customer"}'s ${license.ProductName} trial as a paid, active license. Set the subscription up in Fenerum as usual — once it starts billing, the sync takes the license over automatically.`}
      confirmText="Convert"
      data-feature-code="UI_BackofficeLicense_Row_Convert"
      onConfirm={async () => {
        await updateLicense({
          variables: {
            id: license.Id,
            input: { LicenseStatusCode: LICENSE_STATUS_ACTIVE },
          },
        })
      }}
      {...menuItemProps}
    >
      Convert to Active
    </ActionMenu.ConfirmItem>
  )
}

export default LicenseConvertMenuItem
